import { API_ENDPOINT } from '@/constant/constant.ts'
import { apiRefresh } from '@/api/auth/refresh.ts'
import { useLoginStore } from '@/store/LoginStore.ts'
import { createAlertStreamClient, type AlertStreamClient } from './alertStreamClient.ts'
import { applyAlertStreamEvent } from './alertStreamCache.ts'
import { reportAlertStreamStatus, type AlertStreamKind } from './alertStreamStore.ts'

const STREAM_PATH: Record<AlertStreamKind, string> = {
    alert: '/alert/live/stream',
    bio: '/alert/bio/live/stream'
}

/** 화면 전환 중 훅이 잠깐 해제됐다 다시 붙는 경우에 연결을 끊지 않는다 */
const BIO_RELEASE_DELAY_MS = 2000

const clients: Partial<Record<AlertStreamKind, AlertStreamClient>> = {}
let bioReferences = 0
let bioReleaseTimer: ReturnType<typeof setTimeout> | undefined
let detachGlobalListeners: (() => void) | undefined

const nudgeAll = () => {
    Object.values(clients).forEach((client) => client?.nudge())
}

const stopClient = (kind: AlertStreamKind) => {
    clients[kind]?.stop()
    delete clients[kind]

    if (Object.keys(clients).length === 0) {
        detachGlobalListeners?.()
        detachGlobalListeners = undefined
    }
}

const stopAll = () => {
    const kinds = Object.keys(clients) as AlertStreamKind[]
    kinds.forEach(stopClient)
}

/**
 * 탭 복귀·네트워크 복구에서 재접속 대기를 건너뛰고, 로그아웃하면 모든 스트림을 닫는다.
 * 로그아웃은 `queryClient.clear()` 와 같은 시점에 일어나므로, 늦게 도착한 프레임이 캐시를
 * 되살리지 않도록 토큰이 비는 즉시 스트림을 멈춘다.
 */
const attachGlobalListeners = () => {
    if (detachGlobalListeners) return

    const onVisible = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') nudgeAll()
    }
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisible)
    }
    if (typeof window !== 'undefined') window.addEventListener('online', nudgeAll)

    const unsubscribeLogin = useLoginStore.subscribe((state) => {
        if (!state.accessToken) stopAll()
    })

    detachGlobalListeners = () => {
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', onVisible)
        }
        if (typeof window !== 'undefined') window.removeEventListener('online', nudgeAll)
        unsubscribeLogin()
    }
}

const startClient = (kind: AlertStreamKind) => {
    if (clients[kind]) return

    const client = createAlertStreamClient({
        url: `${API_ENDPOINT}${STREAM_PATH[kind]}`,
        getToken: () => useLoginStore.getState().accessToken,
        refreshToken: apiRefresh,
        onToken: (token) => {
            void useLoginStore.getState().login('', token)
        },
        onEvent: (event, data) => {
            if (clients[kind] === client) applyAlertStreamEvent(event, data)
        },
        onStatus: (status) => reportAlertStreamStatus(kind, status)
    })

    clients[kind] = client
    attachGlobalListeners()
    client.start()
}

/** 긴급·낙상 스트림을 시작한다. 반환값은 정리 함수 */
export const startAlertStreams = (): (() => void) => {
    startClient('alert')
    return () => stopClient('alert')
}

/**
 * 생체 스트림을 구독한다. 생체 좌표는 생체 화면이 열린 클라이언트에만 가야 하므로
 * 훅이 마운트된 동안만 연결하고, 구독자가 모두 해제되면 잠시 뒤 닫는다.
 */
export const acquireBioStream = (): (() => void) => {
    if (bioReleaseTimer) {
        clearTimeout(bioReleaseTimer)
        bioReleaseTimer = undefined
    }

    bioReferences += 1
    startClient('bio')

    let released = false
    return () => {
        if (released) return
        released = true

        bioReferences -= 1
        if (bioReferences > 0) return

        bioReleaseTimer = setTimeout(() => {
            bioReleaseTimer = undefined
            if (bioReferences === 0) stopClient('bio')
        }, BIO_RELEASE_DELAY_MS)
    }
}

export const resetAlertStreamRuntimeForTests = () => {
    if (bioReleaseTimer) clearTimeout(bioReleaseTimer)
    bioReleaseTimer = undefined
    bioReferences = 0
    stopAll()
}
